((root, factory) => {
  const continuationApi = typeof module === 'object' && module.exports ? require('./dim-context-continuation.js') : root?.DIMContextContinuation;
  const api = factory(continuationApi);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.DIMRuntimeBootstrap = api;
    const element = root.document.getElementById('dim-runtime-context');
    if (!element) return;
    try {
      const config = JSON.parse(element.textContent);
      if (config.runtime?.environment === 'development' && !root.DIMDevSession) throw new Error('DEV_SESSION_UNAVAILABLE');
      root.DIMRuntime = api.create(config, {
        contexts: root.DIMRuntimeContext,
        scenes: root.DIMSceneStorage,
        paidOperations: root.DIMPaidOperation,
        devSessions: root.DIMDevSession,
        storage: root.sessionStorage,
        history: root.history,
        indexedDB: root.indexedDB,
        onAuthenticated: () => root.document.dispatchEvent(new CustomEvent('dim:runtime-authenticated')),
        onVerified: () => root.document.dispatchEvent(new CustomEvent('dim:runtime-verified')),
        fetchImpl: root.fetch.bind(root), pagePath: root.location.pathname, pageSearch: root.location.search, pageOrigin: root.location.origin
      });
      const mount = () => {
        const container = root.document.querySelector('main') || root.document.body;
        if (container && root.DIMRuntime?.devSession) {
          const notice = root.DIMRuntime.devSession.mountNotice({ container });
          if (!root.DIMRuntime.devSession.getToken()) notice.show();
        }
      };
      if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', mount, { once: true });
      else mount();
    } catch (error) {
      // A requested successor journey must fail closed, never adopt legacy/live state.
      root.DIMRuntime = Object.freeze({ error, verified: false, enabled: true });
    }
  }
})(typeof window !== 'undefined' ? window : globalThis, continuations => {
  'use strict';
  const copy = value => JSON.parse(JSON.stringify(value));
  const fail = code => { const error = new Error(code); error.code = code; throw error; };
  const contextKeys = ['storefrontId', 'bindingId', 'mode', 'purpose'];
  const sameContext = (a, b) => contextKeys.every(key => typeof a?.[key] === 'string' && a[key] === b?.[key]);

  function create(config, { contexts, scenes, paidOperations, devSessions, storage, indexedDB, history, continuations: configuredContinuations = continuations, fetchImpl = fetch, now = Date.now, pagePath = '/', pageSearch = '', pageOrigin = null, onAuthenticated, onVerified } = {}) {
    if (config?.enabled !== true) return null;
    if (!contexts || !configuredContinuations || !config.runtime) fail('RUNTIME_UNCONFIGURED');
    const runtime = contexts.create(copy(config.runtime));
    let previewContinuation = null;
    if (!config.context && config.staffPreviewShell === true && runtime.environment === 'development') {
      // A private draft needs no published routing metadata. The neutral shell
      // only locates an exact selector or an explicitly prepared signed handoff;
      // it never supplies customer authority or imports a primary draft.
      const shell = { storefrontId: 'dim-main', bindingId: 'primary', mode: 'primary', purpose: 'customer' };
      previewContinuation = configuredContinuations.create({ runtime, context: shell, storage, history, now, pagePath, pageSearch });
      if (previewContinuation.provisionalContext?.purpose === 'staff-preview') {
        config = { ...config, context: shell, pageBinding: { bindingId: 'primary', pageKey: '/' } };
      } else { previewContinuation.dispose(); previewContinuation = null; }
    }
    if (!config.context) {
      // Permit the existing development sign-in/health controls before a fresh
      // primary catalogue has been selected. No draft, paid adapter or customer
      // request is created until the page carries a real bound context.
      if (runtime.environment !== 'development' || config.context !== null || config.pageBinding !== null || !devSessions
        || typeof pageOrigin !== 'string') fail('RUNTIME_UNCONFIGURED');
      let origin;
      try { origin = new URL(pageOrigin); } catch (_) { fail('RUNTIME_UNCONFIGURED'); }
      if (origin.protocol !== 'https:' || origin.origin !== pageOrigin) fail('RUNTIME_UNCONFIGURED');
      let disposed = false;
      const devSession = devSessions.create({ runtime, shop: config.shop, fetchImpl, now });
      const reject = async () => fail(disposed ? 'RUNTIME_DISPOSED' : 'CONTEXT_UNCONFIGURED');
      return Object.freeze({ enabled: true, runtime, devSession, context: null, scene: null, verified: false,
        features: Object.freeze({}), fontCatalogue: Object.freeze([]), verify: reject, authenticatedFetch: reject,
        requireAdmission: reject, prepareTransition: reject, prepareJourneyTransition: reject,
        invalidate() {}, dispose() { if (disposed) return; disposed = true; devSession.dispose(); } });
    }
    const originalContext = copy(config.context);
    const pageBinding = config.pageBinding;
    if (!contextKeys.every(key => typeof originalContext[key] === 'string' && originalContext[key])) fail('CONTEXT_UNCONFIGURED');
    configuredContinuations.validateProvisionalContext(originalContext, 'CONTEXT_UNCONFIGURED');
    if (!pageBinding || typeof pageBinding !== 'object' || Array.isArray(pageBinding) || Object.keys(pageBinding).length !== 2
      || typeof pageBinding.bindingId !== 'string' || !pageBinding.bindingId || pageBinding.bindingId !== originalContext.bindingId
      || typeof pageBinding.pageKey !== 'string' || !pageBinding.pageKey || pageBinding.pageKey.length > 120
      || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(pageBinding.pageKey)
      || typeof pageOrigin !== 'string') fail('CONTEXT_UNCONFIGURED');
    let renderedOrigin;
    try { renderedOrigin = new URL(pageOrigin); } catch (_) { fail('CONTEXT_UNCONFIGURED'); }
    if (renderedOrigin.protocol !== 'https:' || renderedOrigin.origin !== pageOrigin || renderedOrigin.username || renderedOrigin.password || renderedOrigin.hash) fail('CONTEXT_UNCONFIGURED');
    const workerOrigin = new URL(runtime.workerOrigin).origin;
    if (workerOrigin !== runtime.workerOrigin || !workerOrigin.startsWith('https://')) fail('DESTINATION_MISMATCH');
    let disposed = false;
    let epoch = 0;
    let inFlight = null;
    let verifiedUntil = 0;
    let verifiedCapabilities = Object.freeze([]);
    let verifiedDisplay = null;
    let activeContext = Object.freeze(originalContext);
    const continuation = previewContinuation || configuredContinuations.create({ runtime, context: originalContext, storage, history, now, pagePath, pageSearch });
    const requireCurrent = captured => { if (disposed || captured !== epoch) fail('RUNTIME_DISPOSED'); };
    const controllers = new Set();
    const invalidate = () => { epoch++; verifiedUntil = 0; verifiedDisplay = null; verifiedCapabilities = Object.freeze([]); inFlight = null; controllers.forEach(controller => controller.abort()); };
    const devSession = runtime.environment === 'development' && devSessions
      ? devSessions.create({ runtime, shop: config.shop, fetchImpl, now, onInvalidated: invalidate,
        requireStaffAccess: continuation.provisionalContext?.purpose === 'staff-preview',
        onAuthenticated: () => { invalidate(); onAuthenticated?.(); } }) : null;
    const rawAuthenticatedFetch = async (url, options = {}) => {
      requireCurrent(epoch);
      const parsed = new URL(url, workerOrigin);
      if (parsed.origin !== workerOrigin || parsed.username || parsed.password || parsed.hash) fail('DESTINATION_MISMATCH');
      // The development bearer is held only in memory and attached only to the
      // exact Worker origin. Sign-in never retries a paid or earlier request.
      const headers = new Headers(options.headers || {});
      headers.set('X-DIM-Page-Path', pagePath);
      if (parsed.pathname !== '/v1/environment' && parsed.pathname.startsWith('/v1/')) {
        const grant = continuation.currentGrant();
        if (grant) headers.set('X-DIM-Context-Continuation', grant.grant);
      }
      return (devSession ? devSession.authenticatedFetch : fetchImpl)(parsed.href, { ...options, headers, credentials: 'include', redirect: 'error', cache: 'no-store' });
    };
    const boundedJson = async (path, options, captured) => {
      const controller = new AbortController();
      controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), 30000);
      const abort = () => controller.abort();
      options?.signal?.addEventListener?.('abort', abort, { once: true });
      try {
        if (options?.signal?.aborted) controller.abort();
        const response = await rawAuthenticatedFetch(`${workerOrigin}${path}`, { ...options, signal: controller.signal });
        requireCurrent(captured);
        if (!response.ok) {
          let body = null;
          try { body = await response.json(); } catch (_) {}
          const code = typeof body?.code === 'string' && /^[A-Z0-9_]{1,80}$/.test(body.code) ? body.code
            : response.status === 401 || response.status === 403 ? 'DEV_SIGN_IN_REQUIRED' : 'DEV_SERVICE_UNAVAILABLE';
          const error = new Error(code);
          error.code = error.message;
          throw error;
        }
        const value = await response.json();
        requireCurrent(captured);
        return value;
      } finally {
        clearTimeout(timer);
        controllers.delete(controller);
        options?.signal?.removeEventListener?.('abort', abort);
      }
    };
    const verify = async ({ force = false } = {}) => {
      requireCurrent(epoch);
      if (!force && verifiedUntil > now()) return activeContext;
      if (inFlight) return inFlight;
      const captured = epoch;
      const operation = (async () => {
        const identity = await boundedJson('/v1/environment', {}, captured);
        contexts.verifyIdentity(identity, runtime, ['protected-drafts', 'catalogue']);
        const capabilities = Object.freeze((identity.capabilities || []).filter(value => typeof value === 'string'));
        const pendingGrant = continuation.currentGrant();
        const requestContext = { origin: pageOrigin, path: pagePath, bindingId: pageBinding.bindingId, pageKey: pageBinding.pageKey };
        if (continuation.previewSelector !== null) requestContext.preview = continuation.previewSelector;
        // A staff preview grant is supplied only as the protected-request
        // header after its initial selector proof.  The primary-branded Review
        // surface therefore cannot turn an untrusted selector into authority.
        else if (pendingGrant && continuation.provisionalContext?.purpose !== 'staff-preview') requestContext.continuationGrant = pendingGrant.grant;
        const result = await boundedJson('/v1/storefront/context', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestContext) }, captured);
        if (!sameContext(originalContext, result.context) && continuation.provisionalContext === null) fail('CONTEXT_MISMATCH');
        configuredContinuations.validateContext(result.context, 'CONTEXT_MISMATCH');
        if (activeContext.schemaVersion === 'storefront-context@1' && !configuredContinuations.sameAuthorityIdentity(activeContext, result.context)) fail('CONTEXT_MISMATCH');
        requireCurrent(captured);
        activeContext = Object.freeze(copy(continuation.saveVerified({ context: result.context, ...(Object.prototype.hasOwnProperty.call(result, 'continuation') ? { continuation: result.continuation } : {}) })));
        if (result.display !== undefined) {
          if (!result.display || typeof result.display !== 'object' || Array.isArray(result.display) || JSON.stringify(result.display).length > 65536) fail('DISPLAY_MISMATCH');
          verifiedDisplay = copy(result.display);
        } else verifiedDisplay = null;
        verifiedUntil = now() + 30000;
        verifiedCapabilities = capabilities;
        onVerified?.();
        return activeContext;
      })();
      inFlight = operation;
      try { return await operation; }
      catch (error) { verifiedUntil = 0; throw error; }
      finally { if (inFlight === operation) inFlight = null; }
    };
    const authenticatedFetch = async (url, options = {}) => {
      const destination = new URL(url, workerOrigin);
      if (destination.origin !== workerOrigin || destination.username || destination.password || destination.hash) fail('DESTINATION_MISMATCH');
      await verify({ force: true });
      return rawAuthenticatedFetch(url, options);
    };
    const prepareTransition = async targetPath => {
      const current = await verify({ force: true });
      const grant = continuation.readGrant();
      if (!grant) {
        if (!['primary', 'alias'].includes(current.mode) || current.purpose !== 'customer'
          || typeof targetPath !== 'string' || !/^\/(?!\/)[^?#\\\r\n]*$/.test(targetPath) || targetPath.length > 240) fail('CURRENT_CONTINUATION_REQUIRED');
        return Object.freeze({ targetPath, expiresAt: null, status: 'not-required' });
      }
      return continuation.prepareTransition({ targetPath, context: current, continuation: grant });
    };
    const sceneContext = continuation.provisionalContext || originalContext;
    const scene = scenes?.create({ runtime, storefrontId: sceneContext.storefrontId, purpose: sceneContext.purpose, bindingId: sceneContext.bindingId, contextMode: sceneContext.mode, storage, indexedDB });
    const api = {
      enabled: true, runtime, scene, devSession, continuation,
      get context() { return activeContext; },
      get verified() { return !disposed && verifiedUntil > now(); },
      get display() { return !disposed && verifiedUntil > now() && verifiedDisplay ? copy(verifiedDisplay) : null; },
      features: Object.freeze(copy(config.features || {})),
      get capabilities() { return verifiedCapabilities; },
      fontCatalogue: Object.freeze(copy(config.fontCatalogue || [])),
      verify, authenticatedFetch,
      async requireAdmission(selection, { signal } = {}) {
        await verify({ force: true });
        const captured = epoch;
        const response = await boundedJson('/v1/storefront/admit', { method: 'POST', signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ context: activeContext, selection,
          ...(verifiedCapabilities.includes('words-only@1') ? { client_capabilities: ['words_only'] } : {}) }) }, captured);
        if (response.allowed !== true || !sameContext(activeContext, response.context)
          || response.context.storefrontRevision !== activeContext.storefrontRevision
          || response.context.catalogueRevision !== activeContext.catalogueRevision
          || response.context.activationEpoch !== activeContext.activationEpoch) fail('CURRENT_ADMISSION_REQUIRED');
        return Object.freeze(copy(response));
      },
      prepareTransition,
      prepareJourneyTransition({ targetPath } = {}) { return prepareTransition(targetPath); },
      invalidate,
      dispose() { disposed = true; epoch++; verifiedUntil = 0; controllers.forEach(controller => controller.abort()); controllers.clear(); continuation.dispose(); devSession?.dispose(); }
    };
    if (paidOperations) api.paid = paidOperations.create({ runtime: api, storage, now });
    return Object.freeze(api);
  }
  return Object.freeze({ create, sameContext });
});
